import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Gift, Plus, RefreshCw, UserCheck, X } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  PATIENT REFERRALS (Phase 11) — patient-to-patient referral tracker.
//  Status flow: pending → registered → qualified → rewarded
// ═══════════════════════════════════════════════════════════════════

const STATUS_STYLE = {
  pending:    { bg:'#fef3c7', fg:'#92400e' },
  registered: { bg:'#dbeafe', fg:'#1e40af' },
  qualified:  { bg:'#fef3c7', fg:'#92400e' },
  rewarded:   { bg:'#dcfce7', fg:'#166534' },
  rejected:   { bg:'#fee2e2', fg:'#991b1b' },
};

const PatientReferralsPage = ({ API_BASE }) => {
  const [referrals, setReferrals] = useState([]);
  const [patients, setPatients] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(true);
  const [clinicInfo, setClinicInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, lb, ci] = await Promise.all([
        axios.get(`${API_BASE}/patient-referrals`, filter === 'all' ? {} : { params: { status: filter } }),
        axios.get(`${API_BASE}/patients`),
        axios.get(`${API_BASE}/patient-referrals/leaderboard?limit=10`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/admin/clinic-settings`).catch(() => ({ data: null })),
      ]);
      setReferrals(r.data || []);
      setPatients(p.data || []);
      setLeaderboard(lb.data || []);
      setClinicInfo(ci.data);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, filter]);

  const shareOnWhatsApp = (referrerName, referrerPhone) => {
    const clinicName = clinicInfo?.clinicName || 'our clinic';
    const slug = clinicInfo?.slug || '';
    const bookingUrl = slug ? `https://app.oralzy.com/book/${slug}` : 'https://app.oralzy.com';
    const msg = encodeURIComponent(
      `Hi! 👋 ${referrerName} has recommended *${clinicName}* to you. ` +
      `You'll get quality dental care from trusted doctors with high success rates.\n\n` +
      `Book your appointment here: ${bookingUrl}\n\n` +
      `Mention ${referrerName}'s name when you visit!`
    );
    const phone = referrerPhone ? String(referrerPhone).replace(/\D/g, '') : '';
    const url = phone
      ? `https://wa.me/91${phone}?text=${msg}`
      : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank');
  };

  useEffect(() => { load(); }, [load]);

  const autoCheck = async () => {
    try {
      const r = await axios.get(`${API_BASE}/patient-referrals/auto-check`);
      alert(`Scanned ${r.data.scanned}, qualified ${r.data.qualified}`);
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const createReferral = async () => {
    if (!creating.referrerId || !creating.refereeName || !creating.refereePhone)
      return alert('Referrer, referee name + phone required');
    try {
      await axios.post(`${API_BASE}/patient-referrals`, creating);
      setCreating(null); load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const action = async (id, act, billId) => {
    try {
      await axios.put(`${API_BASE}/patient-referrals/${id}/${act}`, billId ? { billId } : {});
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const stats = {
    pending: referrals.filter(r => r.status === 'pending').length,
    registered: referrals.filter(r => r.status === 'registered').length,
    qualified: referrals.filter(r => r.status === 'qualified').length,
    rewarded: referrals.filter(r => r.status === 'rewarded').length,
    totalRewards: referrals.filter(r => r.status === 'rewarded').reduce((a, r) => a + (r.rewardAmount || 0), 0),
  };

  return (
    <div style={{ padding:20 }}>
      <h2 style={{ color: theme.navy, margin:'0 0 16px' }}>🎁 Patient Referrals</h2>

      <div style={{ ...cardStyle, marginBottom:12, background: theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color: theme.navy, lineHeight:1.6 }}>
          <b>How it works:</b> Existing patient → Refers new person → We create pending referral. When new person registers (auto-detected by phone) status becomes 'registered'. Once they pay first bill ≥ threshold, 'qualified'. Admin approves reward → WhatsApp notification to referrer.
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:16 }}>
        <StatCard label="Pending" value={stats.pending} color={theme.warning}/>
        <StatCard label="Registered" value={stats.registered} color={theme.info}/>
        <StatCard label="Qualified" value={stats.qualified} color={theme.secondary}/>
        <StatCard label="Rewarded" value={stats.rewarded} color={theme.success}/>
        <StatCard label="Rewards Paid" value={`₹${stats.totalRewards.toLocaleString('en-IN')}`} color={theme.success}/>
      </div>

      {/* J3 — Top Referrer Leaderboard (growth gamification) */}
      {leaderboard.length > 0 && (
        <div style={{ ...cardStyle, marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <h3 style={{ margin:0, color:theme.navy, fontSize:'1rem' }}>🏆 Top Referrers</h3>
            <button onClick={()=>setShowLeaderboard(!showLeaderboard)}
              style={{ background:'none', border:'none', color:theme.primary, cursor:'pointer', fontSize:'0.8rem', fontWeight:600 }}>
              {showLeaderboard ? 'Hide ▲' : 'Show ▼'}
            </button>
          </div>
          {showLeaderboard && (
            <div style={{ display:'grid', gap:6 }}>
              {leaderboard.map((r, i) => {
                const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`;
                return (
                  <div key={r._id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 10px', borderRadius:8, background: i < 3 ? '#fef3c7' : '#f9fafb', border:'1px solid #f1f3f5' }}>
                    <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                      <span style={{ fontSize:'1.1rem', minWidth:36 }}>{medal}</span>
                      <div>
                        <div style={{ fontWeight:700, fontSize:'0.9rem' }}>{r.referrerName}</div>
                        <div style={{ fontSize:'0.72rem', color:'#666' }}>{r.referrerPhone}</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:14, alignItems:'center' }}>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'0.7rem', color:'#888' }}>Total</div>
                        <div style={{ fontWeight:700 }}>{r.total}</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'0.7rem', color:'#888' }}>Rewarded</div>
                        <div style={{ fontWeight:700, color:theme.success }}>{r.rewarded}</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'0.7rem', color:'#888' }}>Value</div>
                        <div style={{ fontWeight:700, color:theme.success }}>₹{(r.rewardValue || 0).toLocaleString('en-IN')}</div>
                      </div>
                      <button onClick={()=>shareOnWhatsApp(r.referrerName, r.referrerPhone)}
                        style={{ padding:'6px 12px', background:'#25D366', color:'white', border:'none', borderRadius:6, fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
                        📱 Send Link
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        {['all','pending','registered','qualified','rewarded','rejected'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding:'8px 14px', borderRadius:6, cursor:'pointer',
            border:`1px solid ${filter===f ? theme.primary : theme.border}`,
            background: filter===f ? theme.primary : 'white',
            color: filter===f ? 'white' : theme.navy, fontWeight:600, textTransform:'capitalize',
          }}>{f}</button>
        ))}
        <button onClick={autoCheck} style={{ ...btnStyle, background: theme.secondary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
          <RefreshCw size={14}/> Auto-Check
        </button>
        <button onClick={() => setCreating({ referrerId:'', refereeName:'', refereePhone:'', rewardType:'credit', rewardAmount:500, qualifyingThreshold:1000 })}
          style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
          <Plus size={14}/> New Referral
        </button>
      </div>

      <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>
         : referrals.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No referrals</div>
         : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f9fafb' }}>
              <th style={th}>Referrer</th><th style={th}>Referee</th><th style={th}>Phone</th>
              <th style={th}>Reward</th><th style={th}>Threshold</th>
              <th style={th}>Status</th><th style={th}>Action</th>
            </tr></thead>
            <tbody>
              {referrals.map(r => {
                const s = STATUS_STYLE[r.status] || STATUS_STYLE.rejected;
                return (
                  <tr key={r._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                    <td style={{ ...td, fontWeight:600 }}>{r.referrerName}</td>
                    <td style={td}>{r.refereeName}</td>
                    <td style={td}>{r.refereePhone}</td>
                    <td style={td}>₹{r.rewardAmount} ({r.rewardType})</td>
                    <td style={td}>₹{r.qualifyingThreshold}</td>
                    <td style={td}>
                      <span style={{ padding:'3px 8px', borderRadius:10, fontSize:'0.75rem', fontWeight:700, textTransform:'capitalize', background: s.bg, color: s.fg }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={td}>
                      {r.status === 'registered' && <button onClick={() => action(r._id, 'qualify')} style={tinyBtn}>Mark Qualified</button>}
                      {r.status === 'qualified' && <button onClick={() => action(r._id, 'reward')} style={{ ...tinyBtn, color: theme.success, fontWeight:700 }}>🎁 Reward</button>}
                      {['pending','registered','qualified'].includes(r.status) && <button onClick={() => action(r._id, 'reject')} style={{ ...tinyBtn, color: theme.danger }}>Reject</button>}
                      <button onClick={() => shareOnWhatsApp(r.referrerName, r.referrerPhone)}
                        style={{ ...tinyBtn, background:'#25D366', color:'white', border:'none' }} title="Send referrer WhatsApp link to share">
                        📱
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
         )}
      </div>

      {creating && (
        <div style={modalBg} onClick={() => setCreating(null)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: theme.navy, margin:'0 0 12px' }}>New Referral</h3>
            <label style={lbl}>Referring Patient</label>
            <select value={creating.referrerId} onChange={e => setCreating({ ...creating, referrerId: e.target.value })} style={{ ...inputStyle, marginBottom:8 }}>
              <option value="">Select patient…</option>
              {patients.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
            </select>
            <label style={lbl}>Referee Name</label>
            <input value={creating.refereeName} onChange={e => setCreating({ ...creating, refereeName: e.target.value })} style={{ ...inputStyle, marginBottom:8 }}/>
            <label style={lbl}>Referee Phone</label>
            <input value={creating.refereePhone} onChange={e => setCreating({ ...creating, refereePhone: e.target.value })} style={{ ...inputStyle, marginBottom:8 }}/>
            <div style={{ display:'flex', gap:8 }}>
              <div style={{ flex:1 }}>
                <label style={lbl}>Reward Type</label>
                <select value={creating.rewardType} onChange={e => setCreating({ ...creating, rewardType: e.target.value })} style={inputStyle}>
                  <option value="credit">Credit</option>
                  <option value="discount">Discount</option>
                  <option value="gift">Gift</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div style={{ flex:1 }}>
                <label style={lbl}>Reward ₹</label>
                <input type="number" value={creating.rewardAmount} onChange={e => setCreating({ ...creating, rewardAmount: Number(e.target.value) })} style={inputStyle}/>
              </div>
            </div>
            <label style={lbl}>Qualifying Threshold ₹ (min first bill)</label>
            <input type="number" value={creating.qualifyingThreshold} onChange={e => setCreating({ ...creating, qualifyingThreshold: Number(e.target.value) })} style={{ ...inputStyle, marginBottom:12 }}/>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setCreating(null)} style={{ ...btnStyle, flex:1 }}>Cancel</button>
              <button onClick={createReferral} style={{ ...btnStyle, flex:1, background: theme.primary, color:'white' }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div style={{ ...cardStyle, padding:14, borderLeft:`4px solid ${color}` }}>
    <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:1 }}>{label}</div>
    <div style={{ fontSize:22, fontWeight:700, color: theme.navy, marginTop:4 }}>{value}</div>
  </div>
);

const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };
const tinyBtn = { padding:'4px 10px', fontSize:'0.75rem', border:'1px solid #e5e7eb', background:'white', borderRadius:6, cursor:'pointer', marginRight:4 };
const lbl = { fontSize:12, color:'#555', fontWeight:600, marginBottom:4, display:'block' };
const modalBg = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 };
const modalCard = { background:'white', borderRadius:14, padding:24, maxWidth:500, width:'100%', maxHeight:'90vh', overflowY:'auto' };

export default PatientReferralsPage;
